import dayjs from 'dayjs';
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo
} from 'react';
import { ChatMessage } from '../domain/types';
import { Participant, useMeetContext } from '../../Meet';
import { useSessionStoragePersistedState } from '../../SessionStorageState';
import { subscribe, unsubscribe } from '../../EventsBus';
import { ChatEvents } from '../domain/events';
import { useVideoCallContext } from '../../VideoCall/providers/VideoCallContextProvider';


export interface ChatContextProviderProps {
	children: ReactNode;
}

const ChatContext = createContext<
	| {
			messages: ChatMessage[];
			notReaded: boolean;
            me?: Participant;
			expanded: boolean;
			addMessage: (message: ChatMessage) => void;
			alreadyReaded: () => void;
			toggleChat: () => void;
			sendChatMessageToVideoCall: (message: string) => void;
	  }
				| undefined
				>(undefined);

export function ChatContextProvider({ children }: Readonly<ChatContextProviderProps>) {
	const { meet } = useMeetContext();
	const { sendChatMessage } = useVideoCallContext();
	const [messages, setMessages] = useSessionStoragePersistedState<ChatMessage[]>('chat-messages', []);
	const [notReaded, setNotReaded] = useSessionStoragePersistedState<boolean>('chat-not-readed', false);
	const [expanded, setExpanded] = useSessionStoragePersistedState<boolean>('chat-expanded', false);

	const addMessage = useCallback((message: ChatMessage) => {
		setMessages((prev) => [...prev, message]);
	}, []);

	const alreadyReaded = useCallback(() => {
		setNotReaded(false);
	}, []);

	const toggleChat = useCallback(() => {
		setExpanded(!expanded);
	}, [expanded]);

	const me = useMemo(() => {
		if (!meet) {
			return undefined;
		}

		return meet.participants.find(({ memberId }) => memberId === meet.memberId);
	}, [meet]);

	const participant = useMemo(() => {
		if (!meet) {
			return undefined;
		}

		return meet.participants.find(({ memberId }) => memberId !== meet.memberId);
	}, [meet]);

	const chatMessageIncome = useCallback((ev: CustomEvent<{ origin: 'mine' | 'other'; message: string }>) => {
		if (!me || !participant) {
			return;
		}

		const message = {
			avatar: undefined,
			memberId: ev.detail.origin === 'mine' ? me.memberId : participant.memberId,
			timestamp: dayjs().format('HH:mm'),
			fullName: ev.detail.origin === 'mine' ? me.fullName : participant.fullName,
			message: ev.detail.message,
		};

		addMessage(message);
		setNotReaded(true);
	}, [me, participant]);

	const sendChatMessageToVideoCall = useCallback((message: string) => {
		sendChatMessage(message);
	}, [sendChatMessage]);

	const resetChat = useCallback(() => {
		setMessages([]);
		setNotReaded(false);
		setExpanded(false);
	}, []);

	useEffect(() => {
		if (!meet) {
			return;
		}

		subscribe(ChatEvents.chatMessageIncome, chatMessageIncome);
		subscribe(ChatEvents.chatReset, resetChat);
		return () => {
			unsubscribe(ChatEvents.chatMessageIncome, chatMessageIncome);
			unsubscribe(ChatEvents.chatReset, resetChat);
		};
	}, [meet]);


	const value = useMemo(() => {
		return {
			messages,
			me,
			notReaded,
			expanded,
			addMessage,
			alreadyReaded,
			toggleChat,
			sendChatMessageToVideoCall
		};
	}, [messages, me, notReaded, expanded]);

	return (
		<ChatContext.Provider
			value={value}
		>
			{children}
		</ChatContext.Provider>
	);
}

export const useChatContext = () => {
	const chatContext = useContext(ChatContext);
	if (chatContext === undefined) {
		throw new Error('useChatContext must be inside a ChatContextProvider');
	}

	return chatContext;
};
